import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { isAdminRole, canManageRecords } from "../lib/access";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { PriorityBadge, HandoffStatusBadge } from "../components/PriorityBadge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Archive as ArchiveIcon, Search, Trash2, ArrowRight, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso, tz) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-US", { timeZone: tz || "America/Chicago", month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export default function Archive() {
  const { user } = useAuth();
  const isAdmin = isAdminRole(user);
  const canManage = canManageRecords(user);
  const [handoffs, setHandoffs] = useState([]);
  const [q, setQ] = useState("");

  const load = () => api.get("/handoffs", { params: { status: "archived" } }).then((r) => setHandoffs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    await api.delete(`/handoffs/${id}`);
    toast.success("Handoff permanently deleted");
    load();
  };

  const restore = async (id) => {
    await api.post(`/handoffs/${id}/restore`);
    toast.success("Handoff restored to active");
    load();
  };

  const filtered = handoffs.filter((h) => {
    const s = q.toLowerCase();
    return !s || (h.title || "").toLowerCase().includes(s) || h.shift_name.toLowerCase().includes(s) || (h.summary || "").toLowerCase().includes(s);
  });

  return (
    <div className="animate-fade-up">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Records</p>
      <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Archive</h1>
      <p className="mt-1 text-sm text-slate-500">Archived handoffs. Acknowledgement and task history are preserved.</p>

      <div className="relative mt-6 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input data-testid="archive-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search archived handoffs…" className="pl-9" />
      </div>

      <div className="mt-4 space-y-3" data-testid="archive-list">
        {filtered.length === 0 ? (
          <Card className="rounded-md border border-slate-200 bg-white p-10 text-center shadow-sm">
            <ArchiveIcon className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-2 text-sm text-slate-400">No archived handoffs.</p>
          </Card>
        ) : filtered.map((h) => (
          <Card key={h.id} data-testid={`archive-item-${h.id}`} className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-display font-bold text-slate-900" data-testid={`archive-title-${h.id}`}>{h.title}</span>
                  <PriorityBadge priority={h.priority} />
                  <HandoffStatusBadge status={h.status} />
                </div>
                <p className="mt-1 text-sm text-slate-500 line-clamp-2">{h.summary}</p>
                <p className="mt-1 text-xs text-slate-400">From: {h.shift_name}{h.division ? ` · ${h.division}` : ""}</p>
                <div className="mt-2 space-y-0.5 text-xs text-slate-400">
                  {h.completed_by && <p>Completed by <span className="font-semibold text-slate-600">{h.completed_by}</span> on {fmt(h.completed_at, user?.org_timezone)}</p>}
                  {h.archived_by && <p>Archived by <span className="font-semibold text-slate-600">{h.archived_by}</span> on {fmt(h.archived_at, user?.org_timezone)}</p>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Link to={`/handoffs/${h.id}`}>
                  <Button variant="outline" size="sm" className="gap-1 h-8" data-testid={`archive-view-${h.id}`}>
                    View <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                </Link>
                {canManage && (
                  <Button data-testid={`archive-restore-${h.id}`} onClick={() => restore(h.id)} variant="outline" size="sm" className="h-8 gap-1 text-blue-700 border-blue-200 hover:bg-blue-50">
                    <RotateCcw className="h-3.5 w-3.5" /> Restore
                  </Button>
                )}
                {isAdmin && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button data-testid={`archive-delete-${h.id}`} variant="outline" size="sm" className="h-8 gap-1 text-red-600 border-red-200 hover:bg-red-50">
                        <Trash2 className="h-3.5 w-3.5" /> Delete
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Permanently delete handoff?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This permanently deletes the handoff and its history. This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction data-testid={`delete-confirm-${h.id}`} onClick={() => del(h.id)} className="bg-red-600 hover:bg-red-700 text-white">
                          Delete permanently
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
